import React, {useCallback, useEffect, useState} from 'react';
import {View} from 'react-native';

import Text from '/component/base/Text';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {StaticFocusable} from '../../sections/StaticFocusable';

import {PhotoCard} from './PhotoCard';
import {styles} from './PhotosSection.style';
import type {PhotosSectionProps, SlotType} from './PhotosSection.types';

const {photosSection} = ManageMySiteTestIDs;

type PickedImage = {
  uri: string;
  mimeType?: string;
  base64?: string;
};

type LocalSlotState = {
  status: 'empty' | 'preview' | 'uploading' | 'uploaded' | 'error';
  uri: string;
  progress: number;
  imageFile?: PickedImage | null;
};

const makeInitialSlot = (url?: string | null): LocalSlotState => {
  const normalizedUrl = typeof url === 'string' ? url.trim() : '';

  return {
    status: normalizedUrl ? 'uploaded' : 'empty',
    uri: normalizedUrl,
    progress: 0,
    imageFile: null,
  };
};

export const PhotosSection = ({
  beforeUrl = '',
  afterUrl = '',
  onUpload,
  openPicker,
  onDirtyStateChange,
  onCancelUpload,
}: PhotosSectionProps) => {
  const [beforeSlot, setBeforeSlot] = useState<LocalSlotState>(() =>
    makeInitialSlot(beforeUrl),
  );

  const [afterSlot, setAfterSlot] = useState<LocalSlotState>(() =>
    makeInitialSlot(afterUrl),
  );

  useEffect(() => {
    const hasPendingChanges =
      beforeSlot.status === 'preview' || afterSlot.status === 'preview';

    onDirtyStateChange?.(hasPendingChanges);
  }, [beforeSlot.status, afterSlot.status, onDirtyStateChange]);

  useEffect(() => {
    if (
      beforeSlot.status === 'preview' ||
      beforeSlot.status === 'uploading' ||
      beforeSlot.status === 'error'
    ) {
      return;
    }

    setBeforeSlot(makeInitialSlot(beforeUrl));
  }, [beforeUrl, beforeSlot.status]);

  useEffect(() => {
    if (
      afterSlot.status === 'preview' ||
      afterSlot.status === 'uploading' ||
      afterSlot.status === 'error'
    ) {
      return;
    }

    setAfterSlot(makeInitialSlot(afterUrl));
  }, [afterUrl, afterSlot.status]);

  const getSlotHelpers = useCallback(
    (type: SlotType) => ({
      slot: type === 'before' ? beforeSlot : afterSlot,
      setSlot: type === 'before' ? setBeforeSlot : setAfterSlot,
      originalUrl: type === 'before' ? beforeUrl : afterUrl,
    }),
    [beforeSlot, afterSlot, beforeUrl, afterUrl],
  );

  const handlePick = useCallback(
    (type: SlotType) => {
      openPicker('transformation', image => {
        const {setSlot} = getSlotHelpers(type);

        const normalizedImage: PickedImage =
          typeof image === 'string'
            ? {
                uri: image.trim(),
                mimeType: 'image/jpeg',
                base64: '',
              }
            : {
                uri: image?.uri?.trim?.() || '',
                mimeType: image?.mimeType,
                base64: image?.base64,
              };

        if (!normalizedImage.uri) {
          return;
        }

        setSlot({
          status: 'preview',
          uri: normalizedImage.uri,
          progress: 0,
          imageFile: normalizedImage,
        });
      });
    },
    [openPicker, getSlotHelpers],
  );

  const uploadSlot = useCallback(
    async (type: SlotType) => {
      const {slot, setSlot} = getSlotHelpers(type);

      if (!slot.imageFile?.uri) {
        return;
      }

      setSlot(prev => ({
        ...prev,
        status: 'uploading',
        progress: 0,
      }));

      try {
        await onUpload(type, slot.imageFile);

        setSlot(prev => ({
          ...prev,
          status: 'uploaded',
          progress: 100,
          imageFile: null,
        }));
      } catch {
        setSlot(prev => ({
          ...prev,
          status: 'error',
          progress: 0,
        }));
      }
    },
    [getSlotHelpers, onUpload],
  );

  const handleConfirm = useCallback(
    async (type: SlotType) => {
      await uploadSlot(type);
    },
    [uploadSlot],
  );

  const handleRetry = useCallback(
    async (type: SlotType) => {
      await uploadSlot(type);
    },
    [uploadSlot],
  );

  const handleCancel = useCallback(
    (type: SlotType) => {
      const {setSlot, originalUrl} = getSlotHelpers(type);

      const newSlot = makeInitialSlot(originalUrl);
      setSlot(newSlot);
      onCancelUpload?.(type);
    },
    [getSlotHelpers, onCancelUpload],
  );

  return (
    <View
      style={styles.section}
      testID={photosSection.section}
      accessible={false}>
      <StaticFocusable
        testID={photosSection.sectionTitle}
        role="header"
        label="Transformation photos"
        style={styles.sectionTitle}>
        <Text
          allowFontScaling={true}
          maxFontSizeMultiplier={2}
          testID={photosSection.sectionTitleText}
          accessible={false}
          variant="heading"
          style={styles.sectionTitle}
          isBold>
          {tt('photos', 'Transformation Photos')}
        </Text>
      </StaticFocusable>

      <View style={styles.photoGrid} accessible={false}>
        {(['before', 'after'] as SlotType[]).map(type => {
          const slot = type === 'before' ? beforeSlot : afterSlot;

          return (
            <View key={type} style={{flex: 1}} accessible={false}>
              <PhotoCard
                type={type}
                slot={slot}
                onPick={() => handlePick(type)}
                onConfirm={() => handleConfirm(type)}
                onCancel={() => handleCancel(type)}
                onRetry={() => handleRetry(type)}
              />
            </View>
          );
        })}
      </View>
    </View>
  );
};

export default PhotosSection;
